import type { LedgerEvent } from './types';

function normAddr(a: string | undefined): string | null {
  if (!a) return null;
  const t = a.trim().toLowerCase();
  return t.startsWith('0x') && t.length === 42 ? t : null;
}

export type CronosWalletTouchingFilterStats = {
  /** Rows set to IGNORE_NON_TAX — transfer does not involve any configured owned address. */
  rowsSuppressed: number;
};

/**
 * Token transfers that appear in a **full-tx** or merged view may include router↔pool legs with **no**
 * user on `from` or `to`. Wallet-scoped explorer fetches normally omit those; compiled CSV merges can
 * still inject them. Such rows are not the user’s economic action — drop from FIFO/tax treatment.
 *
 * A row **passes** if `fromAddress`, `toAddress`, or `primaryWalletAddress` matches an owned wallet.
 * Rows with **no** chain addresses at all are left unchanged (legacy / incomplete rows).
 */
export function applyCronosWalletTouchingFilter(
  events: LedgerEvent[],
  ownedAddresses: Set<string>
): { stats: CronosWalletTouchingFilterStats } {
  const stats: CronosWalletTouchingFilterStats = { rowsSuppressed: 0 };
  if (ownedAddresses.size === 0) {
    return { stats };
  }

  for (const ev of events) {
    if (ev.source !== 'cronos' && ev.source !== 'arbitrum') continue;
    if (ev.classification === 'IGNORE_NON_TAX') continue;

    const f = normAddr(ev.fromAddress);
    const t = normAddr(ev.toAddress);
    const p = normAddr(ev.primaryWalletAddress);

    if (!f && !t && !p) continue;

    let touches = false;
    if (f && ownedAddresses.has(f)) touches = true;
    if (t && ownedAddresses.has(t)) touches = true;
    if (p && ownedAddresses.has(p)) touches = true;

    if (touches) continue;

    ev.classification = 'IGNORE_NON_TAX';
    ev.fiTaxCategory = 'IGNORE';
    ev.fiSubreason =
      ev.source === 'arbitrum' ? 'ARBITRUM_TRANSFER_NO_OWNED_WALLET' : 'CRONOS_TRANSFER_NO_OWNED_WALLET';
    ev.explanation = [
      ev.explanation,
      'Transfer does not involve a configured owned wallet on from/to/primary — treated as contract-only or out-of-scope leg.',
    ]
      .filter(Boolean)
      .join(' ');
    const prev = ev.reviewFlags ?? [];
    ev.reviewFlags = Array.from(
      new Set([...prev, 'cronos_no_owned_wallet_on_transfer', 'contract_only_leg_suppressed'])
    );
    stats.rowsSuppressed++;
  }

  return { stats };
}
